import moment from 'moment';
import axios from 'axios';
import sha256 from 'crypto-js/sha256';
import base64 from 'crypto-js/enc-base64';

/**
 * Centralized Authentication Service
 * Manages authentication state, token refresh, and session monitoring
 * Independent of Vue components for use in router guards and main.js
 */
class AuthService {
  constructor() {
    this.refreshTimer = null;
    this.refreshTokenPeriodMs = 90000; // 90 seconds before expiry
    this.sessionExpiredCallback = null;
    this.config = null;
  }

  /**
   * Initialize with OAuth configuration
   */
  configure(config) {
    this.config = {
      clientId: config.clientId,
      scope: config.scope,
      authorizeUrl: config.authorizeUrl,
      tokenUrl: config.tokenUrl,
      logoutUrl: config.logoutUrl,
      redirectUri: config.redirectUri || window.location.origin,
    };
  }

  /**
   * Get credentials from localStorage
   */
  getCredentials(clientId) {
    try {
      const cache = JSON.parse(localStorage.getItem('credential-cache')) || {};
      const cred = cache[clientId];
      
      if (!cred) {
        return null;
      }

      // Check if expired
      if (moment(cred.$expires_on) < moment()) {
        this.deleteCredentials(clientId);
        return null;
      }

      return cred;
    } catch (error) {
      console.error('[Auth Service] Error reading credentials:', error);
      return null;
    }
  }

  /**
   * Delete credentials from localStorage
   */
  deleteCredentials(clientId) {
    try {
      const cache = JSON.parse(localStorage.getItem('credential-cache')) || {};
      delete cache[clientId];
      localStorage.setItem('credential-cache', JSON.stringify(cache));
      
      // Notify Vue components that credentials have been deleted
      window.dispatchEvent(new CustomEvent('credentials-updated', {
        detail: { clientId, timestamp: Date.now() }
      }));
    } catch (error) {
      console.error('[Auth Service] Error deleting credentials:', error);
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(clientId) {
    const credentials = this.getCredentials(clientId);
    return !!credentials && !!credentials.access_token;
  }

  /**
   * Calculate milliseconds until token should be refreshed
   */
  getMsUntilRefresh(credentials) {
    if (!credentials || !credentials.expires_on) {
      return -Infinity;
    }
    
    const expiresOn = moment(credentials.expires_on);
    const now = moment();
    const msUntilExpiry = expiresOn.diff(now);
    return msUntilExpiry - this.refreshTokenPeriodMs;
  }

  /**
   * Refresh the access token using refresh token
   */
  async refreshToken(clientId, tokenUrl, scope) {
    const credentials = this.getCredentials(clientId);
    
    if (!credentials || !credentials.refresh_token) {
      console.error('[Auth Service] Cannot refresh - no refresh token available');
      return false;
    }

    const formData = new FormData();
    formData.append('client_id', clientId);
    formData.append('scope', scope);
    formData.append('refresh_token', credentials.refresh_token);
    formData.append('grant_type', 'refresh_token');

    try {
      const response = await axios({
        method: 'post',
        url: tokenUrl,
        data: formData,
        // Skip the global interceptor for token refresh
        skipAuthInterceptor: true,
      });

      // Extend the new credentials with timestamp and expiry
      const newCredentials = {
        ...response.data,
        time_stamp: moment().toISOString(),
        expires_on: moment().add(response.data.expires_in, 'seconds').toISOString(),
      };

      // Update in localStorage using the existing structure
      const cache = JSON.parse(localStorage.getItem('credential-cache')) || {};
      const decode = this.parseJwt(newCredentials.access_token);
      
      cache[clientId] = {
        ...newCredentials,
        $expires_on: moment().add(newCredentials.expires_in, 'seconds').format(),
        'jwt-decode': decode,
        logout_url: credentials.logout_url, // Preserve logout URL
      };

      localStorage.setItem('credential-cache', JSON.stringify(cache));
      
      // Trigger custom event to notify Vue components of credential update
      // Note: storage event only fires in other tabs, so we use a custom event
      window.dispatchEvent(new CustomEvent('credentials-updated', {
        detail: { clientId, timestamp: Date.now() }
      }));
      
      
      return true;
    } catch (error) {
      
      if (error.response?.status === 400 || error.response?.status === 401) {
        this.deleteCredentials(clientId);
        
        // Trigger session expired callback
        if (this.sessionExpiredCallback) {
          this.sessionExpiredCallback();
        }
        
        return false;
      }
      
      // Network error - keep credentials and retry later
      console.error('[Auth Service] Token refresh failed:', error.response?.status, error.response?.data);
      return false;
    }
  }

  /**
   * Start monitoring token expiration and auto-refresh
   */
  startRefreshMonitoring(clientId, tokenUrl, scope) {
    // Clear any existing timer
    this.stopRefreshMonitoring();

    const checkAndRefresh = async () => {
      const credentials = this.getCredentials(clientId);
      
      if (!credentials) {
        this.stopRefreshMonitoring();
        return;
      }

      const msUntilRefresh = this.getMsUntilRefresh(credentials);
      
      if (msUntilRefresh <= 0) {
        // We're in the refresh window
        await this.refreshToken(clientId, tokenUrl, scope);
      }
    };

    // Check immediately, then every 10 seconds
    checkAndRefresh();
    this.refreshTimer = setInterval(checkAndRefresh, 10000);
  }

  /**
   * Stop monitoring token expiration
   */
  stopRefreshMonitoring() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Set callback for when session expires
   */
  onSessionExpired(callback) {
    this.sessionExpiredCallback = callback;
  }

  /**
   * Parse JWT token (simple implementation without full validation)
   */
  parseJwt(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('[Auth Service] Error parsing JWT:', error);
      return {};
    }
  }

  /**
   * Generate code verifier and challenge for PKCE
   */
  generatePKCE() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let verifier = '';
    for (let i = 0; i < 128; i++) {
      verifier += chars[Math.floor(Math.random() * chars.length)];
    }
    
    const challenge = base64.stringify(sha256(verifier))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
    
    return { verifier, challenge };
  }

  /**
   * Generate OAuth state object
   */
  generateState() {
    return {
      state: Math.random().toString(36).substring(2, 15),
      path: window.location.href,
      timestamp: Date.now(),
    };
  }

  /**
   * Build OAuth login URL
   */
  buildLoginUrl(prompt = null) {
    if (!this.config) {
      throw new Error('Auth service not configured');
    }

    const pkce = this.generatePKCE();
    const stateObj = this.generateState();
    const stateId = stateObj.state;

    // Store state and PKCE in localStorage for callback
    const stateData = {
      state: stateId,
      path: stateObj.path,
      client_id: this.config.clientId,
      token_uri: this.config.tokenUrl,
      code_verifier: pkce.verifier,
      client_secret: null,
      type: 'pkce',
      scope: this.config.scope,
      redirect_uri: this.config.redirectUri,
      logout_url: this.config.logoutUrl,
      timestamp: stateObj.timestamp,
    };

    localStorage.setItem(`oauth_state_${stateId}`, JSON.stringify(stateData));

    // Build URL
    const params = new URLSearchParams({
      client_id: this.config.clientId,
      response_type: 'code',
      redirect_uri: this.config.redirectUri,
      scope: `${this.config.scope} offline_access`,
      state: stateId,
      code_challenge: pkce.challenge,
      code_challenge_method: 'S256',
    });

    // Only add prompt if explicitly specified
    if (prompt) {
      params.set('prompt', prompt);
    }

    return `${this.config.authorizeUrl}?${params.toString()}`;
  }

  /**
   * Check if current URL is an OAuth callback
   */
  isCallback() {
    const params = new URLSearchParams(window.location.search);
    return params.has('code') && params.has('state');
  }

  /**
   * Check if current URL has an OAuth error
   */
  hasCallbackError() {
    const params = new URLSearchParams(window.location.search);
    return params.has('error');
  }

  /**
   * Get error details from callback
   */
  getCallbackError() {
    const params = new URLSearchParams(window.location.search);
    return {
      error: params.get('error'),
      errorDescription: params.get('error_description'),
    };
  }

  /**
   * Handle OAuth callback and exchange code for tokens
   */
  async handleCallback() {
    
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const stateId = params.get('state');

    if (!code || !stateId) {
      throw new Error('Missing code or state in callback');
    }

    // Retrieve stored state
    const storedStateJson = localStorage.getItem(`oauth_state_${stateId}`);
    if (!storedStateJson) {
      throw new Error('State not found - possible CSRF attack or expired session');
    }

    const storedState = JSON.parse(storedStateJson);
    
    // Exchange code for tokens
    const formData = new FormData();
    formData.append('grant_type', 'authorization_code');
    formData.append('client_id', storedState.client_id);
    formData.append('redirect_uri', storedState.redirect_uri);
    formData.append('code', code);
    formData.append('code_verifier', storedState.code_verifier);
    formData.append('scope', `${storedState.scope} offline_access`);

    try {
      const response = await axios({
        method: 'post',
        url: storedState.token_uri,
        data: formData,
      });

      // Process and store credentials
      const credentials = {
        ...response.data,
        time_stamp: moment().toISOString(),
        expires_on: moment().add(response.data.expires_in, 'seconds').toISOString(),
      };

      // Store in localStorage
      const cache = JSON.parse(localStorage.getItem('credential-cache')) || {};
      const decode = this.parseJwt(credentials.access_token);
      
      cache[storedState.client_id] = {
        ...credentials,
        $expires_on: moment().add(credentials.expires_in, 'seconds').format(),
        'jwt-decode': decode,
        logout_url: storedState.logout_url,
      };

      localStorage.setItem('credential-cache', JSON.stringify(cache));

      // Clean up state
      localStorage.removeItem(`oauth_state_${stateId}`);

      
      // Return the original path to redirect to
      return storedState.path;

    } catch (error) {
      console.error('[Auth Service] Token exchange failed:', error);
      localStorage.removeItem(`oauth_state_${stateId}`);
      throw error;
    }
  }

  /**
   * Redirect to Azure AD login
   * @param {string} prompt - Optional prompt parameter ('login', 'select_account', 'consent', or null for default behavior)
   */
  redirectToLogin(prompt = null) {
    const loginUrl = this.buildLoginUrl(prompt);
    window.location.href = loginUrl;
  }
}

// Export singleton instance
export default new AuthService();